import { useEffect, useState } from 'react'
import styled from 'styled-components'
import DefaultLayout from '../components/molecules/common/DefaultLayout'
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import WaveVisualizer from '../components/molecules/WaveVisualizer';
import LoadingDots from '../components/atoms/LoadingDots'
import Text from '../components/atoms/Text';
import { useBack } from '../hooks/useBack'
import { useConversationRecorder } from '../hooks/useConversationRecorder';
import { uploadConversationAudio } from '../api/conversation';
import { useNavigate } from 'react-router-dom';
import MainButton from '../components/atoms/MainButton';
import { tutorialPageKeys } from '../data/tutorialConfig';
import { useTutorial } from '../hooks/useTutorial';
import { tutorialImages } from '../data/tutorialImages';
import TutorialOverlay from '../components/molecules/auth/TutorialOverlay';
import { logEvent } from '../utils/eventLogger';
import RegistrationGuard from '../components/molecules/auth/RegistrationGuard';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center; // 세로 중앙
  align-items: center; // 가로 중앙
  width: 100%;
  min-height: calc(100dvh - 56px - 72px - 60px); // header + button 높이 제외
  position: relative;
`
const RecordInfo = styled.div`
  width: 100%;
`
const MicButton = styled.button`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${(props) => props.theme.colors.secondary};
  border: none;
  cursor: pointer;
  position: fixed;
  bottom: 60px;
  left: 50%;
  transform: translateX(-50%);
`
const MicIcon = styled(MicRoundedIcon)`
  color: ${(props) => props.theme.colors.text.inverse};
`
const StopIcon = styled(StopRoundedIcon)`
  color: ${(props) => props.theme.colors.text.inverse};
`
const LoadingWrapper = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 0 20px;
  width: 100%;
`
const TextWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`
const GuideMessage = styled.div`
  word-break: keep-all;
`
const ButtonWrapper = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 600px; 
  padding: 0 20px;
`
const ConversationRecord = () => {
  // 튜토리얼 훅
  const page = tutorialPageKeys.CONVERSATION_RECORD;
  const { show, complete } = useTutorial(page);
  const datas = tutorialImages.CONVERSATION_RECORD.steps;

  const [status, setStatus] = useState("idle");

  // 뒤로가기 훅 호출
  const goBack = useBack();
  const navigate = useNavigate();

  const [time, setTime] = useState(0); // 초 단위 시간

  const {
    startRecording,
    stopRecording,
  } = useConversationRecorder({
    onStatusChange: setStatus,
  });

  const handleMicClick = () => {
    if  (status === "idle" || status === "error") {
      startRecording();
    } else if (status === "recording") {
      handleSave();
    } 
  }

  const handleSave = async() => {
    try  {
      setStatus("processing")
      const audioBlob = await stopRecording(); // hook에서 녹음 종료후 Blob 반환
      const formData = new FormData();
      const extension = audioBlob.type.split("/")[1];
      formData.append("audioFile", audioBlob, `recording.${extension}`);

      const rlt = await uploadConversationAudio(formData);
      setStatus("completed");

      // 로그 기록
      logEvent("recording_save", {
        hrId: rlt.conversationId
      })

    } catch(err) {
      // console.log(err)
      console.error(err);
      alert("대화 내용 저장에 실패했어요. 다시 시도해 주세요.")
      setStatus("idle")
    }
  }
  
  // 타이머 동작
  useEffect(() => {
    let timerId;

    if (status === "recording") {
      timerId = setInterval(() => {
        setTime(prev => prev + 1);
      }, 1000);
    } else if (status === "paused" || status === "idle" || status === "processing") {
      clearInterval(timerId);
    }

    return () => clearInterval(timerId);
  }, [status]);
  
  // 리셋 시 시간 초기화
  useEffect(() => {
    if (status === "idle") {
      setTime(0);
    }
  }, [status]);
  
  const formatTime = (seconds) => {
    const mm = Math.floor(seconds / 60).toString().padStart(2, '0');
    const ss = (seconds % 60).toString().padStart(2, '0');
    return `${mm}:${ss}`;
  }

  return (
    <DefaultLayout
      headerType="sub"
      title="대화 기록"
      onBack={goBack}>
      <RegistrationGuard>
         <Wrapper>
          <>
            {(status === "idle" || status === "error") && (
              <Text variant="headline3" weight="medium">
                아이와 나누는 대화를 기록해보세요.
              </Text> 
            )}

            {(status === "recording") && (
              <RecordInfo>
                <WaveVisualizer status={status} />
                <Text variant="headline2" weight="medium" color="alternative">
                  {formatTime(time)}
                </Text>
              </RecordInfo> 
            )}
            
              
            {(status === "idle" || status === "error" || status === "recording") && (
              <MicButton onClick={handleMicClick}>
                {(status === "idle" || status === "error") && <MicIcon sx={{ fontSize: 36 }} />}
                {status === "recording" && <StopIcon sx={{ fontSize: 36 }} />}
              </MicButton>
            )}

            {status === "processing" && 
              <LoadingWrapper>
                <LoadingDots/>
              </LoadingWrapper>
            }

            {status === "completed" && 
              <>
                <TextWrapper>
                  <Text variant="headline3" weight="medium">대화 내용을 저장하고 있어요.</Text>
                  <GuideMessage>
                    <Text as="p" variant="body2" color="alternative">대화 길이나 이용 환경에 따라 시간이 조금 걸릴 수 있어요. </Text>
                    <Text as="p" variant="body2" color="alternative">저장된 내용은 “대화노트”에서 언제든지 확인하실 수 있어요.</Text>
                  </GuideMessage>
                  
                </TextWrapper>
                <ButtonWrapper>
                  <MainButton
                    $variant="primary"
                    title="홈으로 가기"
                    onClick={() => navigate('/')}/>
                </ButtonWrapper>
              </>
            }
          </>
        </Wrapper>
      </RegistrationGuard>
      {show && <TutorialOverlay datas={datas} onClose={complete} />}
    </DefaultLayout>
  )
}

export default ConversationRecord
